import React, { useRef, useEffect, useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import {
  MdShoppingBag,
  MdLocalShipping,
  MdSecurity,
  MdHeadsetMic,
  MdStar,
  MdPhone,
  MdLocationOn,
  MdEmail,
  MdArrowForward,
  MdCheck,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { useCraftStore } from "./CraftStoreContext";
import { AWS_URL } from "../../../services/http.service";
import { ImageChangeButton, resolveImageUrl } from "./ImageUpload";

/**
 * InlineEditable Component
 * Provides seamless direct inline text editing on the visual builder canvas.
 * - In Edit Mode: Users can click and type directly into any text on the canvas.
 * - Stops mouse/pointer event propagation so selecting or typing text doesn't trigger node dragging.
 * - In Preview Mode / Live Storefront: Renders as standard semantic HTML tags.
 */
export const InlineEditable = ({
  value = "",
  propKey,
  setProp,
  enabled = true,
  selected = false,
  as: Tag = "span",
  multiline = false,
  className = "",
  style = {},
  placeholder = "Click to edit...",
}) => {
  const contentRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);

  // Sync external prop changes into DOM if not currently user typing
  useEffect(() => {
    if (contentRef.current && !isEditing) {
      if (contentRef.current.innerText !== (value ?? "")) {
        contentRef.current.innerText = value ?? "";
      }
    }
  }, [value, isEditing]);

  // When preview mode or editor disabled, render regular HTML element
  if (!enabled) {
    return (
      <Tag className={className} style={style}>
        {value || ""}
      </Tag>
    );
  }

  const handleBlur = (e) => {
    setIsEditing(false);
    const newText = e.currentTarget.innerText.trim();
    if (setProp && propKey) {
      setProp((props) => {
        props[propKey] = newText !== "" ? newText : placeholder;
      });
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      e.currentTarget.blur();
    }
  };

  return (
    <Tag
      ref={contentRef}
      contentEditable={enabled}
      suppressContentEditableWarning
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className={`${className} outline-none cursor-text transition-all ${
        selected
          ? "hover:bg-indigo-500/15 focus:bg-indigo-500/20 focus:ring-2 focus:ring-indigo-400/80 rounded px-1 -mx-1"
          : "hover:outline-dashed hover:outline-1 hover:outline-indigo-400/50"
      }`}
      style={{
        ...style,
        minWidth: "1ch",
        wordBreak: "break-word",
      }}
      title="Click to edit text directly inline"
    >
      {value || ""}
    </Tag>
  );
};

// ─── 1. CONTAINER COMPONENT ───────────────────────────────────────────
export const Container = ({
  children,
  background = "#ffffff",
  padding = 24,
  margin = 0,
  borderRadius = 12,
  borderWidth = 0,
  borderColor = "#e5e7eb",
  shadow = "none",
  flexDirection = "column",
  alignItems = "stretch",
  justifyContent = "flex-start",
  gap = 16,
  minHeight = "auto",
  width = "100%",
  maxWidth = "100%",
  className = "",
}) => {
  const {
    id,
    connectors: { connect, drag },
    selected,
    hovered,
  } = useNode((node) => ({
    id: node.id,
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const isRoot = id === "ROOT";

  const shadowClasses = {
    none: "",
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
  };

  const setNodeRef = React.useCallback(
    (ref) => {
      if (ref) {
        isRoot ? connect(ref) : connect(drag(ref));
      }
    },
    [connect, drag, isRoot]
  );

  return (
    <div
      ref={setNodeRef}
      className={`transition-all duration-200 ${shadowClasses[shadow] || ""} ${className} ${
        !isRoot && selected
          ? "ring-2 ring-indigo-500 ring-offset-2"
          : !isRoot && hovered
          ? "ring-1 ring-indigo-300"
          : ""
      }`}
      style={{
        backgroundColor: background,
        padding: `${padding}px`,
        margin: `${margin}px auto`,
        borderRadius: `${borderRadius}px`,
        border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : "none",
        display: "flex",
        flexDirection,
        alignItems,
        justifyContent,
        gap: `${gap}px`,
        minHeight,
        width,
        maxWidth,
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
};

Container.craft = {
  displayName: "Container Section",
  props: {
    background: "#ffffff",
    padding: 24,
    margin: 0,
    borderRadius: 12,
    borderWidth: 0,
    borderColor: "#e5e7eb",
    shadow: "none",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    gap: 16,
    minHeight: "auto",
    width: "100%",
    maxWidth: "100%",
  },
  rules: {
    canDrag: (node) => node.id !== "ROOT",
    canDrop: () => true,
    canMoveIn: () => true,
    canMoveOut: () => true,
  },
};

// ─── 2. TEXT COMPONENT ────────────────────────────────────────────────
export const Text = ({
  text = "Your custom text here",
  tag = "p",
  fontSize = 16,
  fontWeight = "400",
  color = "#1f2937",
  textAlign = "left",
  lineHeight = 1.5,
  margin = 0,
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`transition-all ${
        selected ? "ring-2 ring-indigo-500 rounded p-1" : hovered ? "ring-1 ring-indigo-300 rounded p-1" : ""
      }`}
      style={{
        fontSize: `${fontSize}px`,
        fontWeight,
        color,
        textAlign,
        lineHeight,
        margin: `${margin}px 0`,
      }}
    >
      <InlineEditable
        value={text}
        propKey="text"
        setProp={setProp}
        enabled={enabled}
        selected={selected}
        as={tag}
        multiline={tag === "p" || tag === "div"}
        placeholder="Type custom text..."
      />
    </div>
  );
};

Text.craft = {
  displayName: "Text / Heading",
  props: {
    text: "Your custom text here",
    tag: "p",
    fontSize: 16,
    fontWeight: "400",
    color: "#1f2937",
    textAlign: "left",
    lineHeight: 1.5,
    margin: 0,
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 3. BUTTON COMPONENT ──────────────────────────────────────────────
export const Button = ({
  text = "Shop Now",
  backgroundColor = "#4f46e5",
  textColor = "#ffffff",
  fontSize = 15,
  paddingX = 24,
  paddingY = 12,
  borderRadius = 8,
  buttonStyle = "rounded",
  link = "#",
  fullWidth = false,
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const radius =
    buttonStyle === "pill"
      ? "9999px"
      : buttonStyle === "square"
      ? "0px"
      : `${borderRadius}px`;

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`inline-block ${fullWidth ? "w-full" : "w-auto"}`}
    >
      <button
        type="button"
        className={`font-semibold inline-flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 shadow-sm ${
          fullWidth ? "w-full" : "w-auto"
        } ${selected ? "ring-2 ring-indigo-500 ring-offset-2" : hovered ? "ring-1 ring-indigo-300" : ""}`}
        style={{
          backgroundColor,
          color: textColor,
          fontSize: `${fontSize}px`,
          padding: `${paddingY}px ${paddingX}px`,
          borderRadius: radius,
        }}
        onClick={(e) => {
          if (!enabled && link && link !== "#") {
            window.location.href = link;
          }
        }}
      >
        <InlineEditable
          value={text}
          propKey="text"
          setProp={setProp}
          enabled={enabled}
          selected={selected}
          as="span"
          placeholder="Button Text"
        />
        <MdArrowForward size={16} />
      </button>
    </div>
  );
};

Button.craft = {
  displayName: "Action Button",
  props: {
    text: "Shop Now",
    backgroundColor: "#4f46e5",
    textColor: "#ffffff",
    fontSize: 15,
    paddingX: 24,
    paddingY: 12,
    borderRadius: 8,
    buttonStyle: "rounded",
    link: "#",
    fullWidth: false,
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 4. IMAGE COMPONENT ───────────────────────────────────────────────
export const Image = ({
  src = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&auto=format&fit=crop&q=80",
  alt = "Custom Image",
  width = "100%",
  height = "320px",
  objectFit = "cover",
  borderRadius = 12,
  shadow = "md",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));
  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const shadowClasses = {
    none: "",
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
  };

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`relative overflow-hidden transition-all ${shadowClasses[shadow] || ""} ${
        selected ? "ring-2 ring-indigo-500 ring-offset-2" : hovered ? "ring-1 ring-indigo-300" : ""
      }`}
      style={{
        width,
        maxWidth: "100%",
        height,
        borderRadius: `${borderRadius}px`,
      }}
    >
      <ImageChangeButton
        enabled={enabled}
        onUploaded={(url) => setProp((p) => { p.src = url; })}
      />
      <img
        src={resolveImageUrl(src)}
        alt={alt}
        className="w-full h-full pointer-events-none select-none"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{ objectFit }}
      />
    </div>
  );
};

Image.craft = {
  displayName: "Image Element",
  props: {
    src: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&auto=format&fit=crop&q=80",
    alt: "Custom Image",
    width: "100%",
    height: "320px",
    objectFit: "cover",
    borderRadius: 12,
    shadow: "md",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 5. HERO BANNER BLOCK ─────────────────────────────────────────────
export const HeroBanner = ({
  eyebrow = "✨ NEW ARRIVALS 2026",
  title = "Discover Handcrafted Luxury & Modern Living",
  subtitle = "Explore our premium curated collection designed with utmost quality, style, and care for you.",
  primaryButtonText = "Explore Collection",
  secondaryButtonText = "View Special Offers",
  primaryColor = "#4f46e5",
  secondaryColor = "#ffffff",
  textColor = "#ffffff",
  bgGradient = "from-slate-900 via-indigo-950 to-slate-900",
  bgImage = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&auto=format&fit=crop&q=80",
  overlayOpacity = 65,
  minHeight = "480px",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`relative w-full rounded-2xl overflow-hidden my-4 shadow-xl transition-all ${
        selected ? "ring-4 ring-indigo-500" : hovered ? "ring-2 ring-indigo-300" : ""
      }`}
      style={{ minHeight }}
    >
      {/* Background Image */}
      <ImageChangeButton
        enabled={enabled}
        label="Change background"
        onUploaded={(url) => setProp((p) => { p.bgImage = url; })}
      />
      {bgImage && (
        <img
          src={resolveImageUrl(bgImage)}
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
      )}

      {/* Gradient / Dark Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${bgGradient}`}
        style={{ opacity: overlayOpacity / 100 }}
      />

      {/* Content Container */}
      <div className="relative z-10 flex flex-col justify-center items-start h-full px-6 sm:px-12 md:px-16 py-16 max-w-4xl">
        {(eyebrow || enabled) && (
          <div
            className="inline-block px-3 py-1 mb-4 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-md"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              color: "#fbbf24",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <InlineEditable
              value={eyebrow}
              propKey="eyebrow"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Tag / Eyebrow text"
            />
          </div>
        )}

        <h1
          className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-4 tracking-tight"
          style={{ color: textColor }}
        >
          <InlineEditable
            value={title}
            propKey="title"
            setProp={setProp}
            enabled={enabled}
            selected={selected}
            as="span"
            multiline
            placeholder="Headline Title..."
          />
        </h1>

        <p
          className="text-base sm:text-lg mb-8 max-w-2xl leading-relaxed"
          style={{ color: "rgba(255, 255, 255, 0.85)" }}
        >
          <InlineEditable
            value={subtitle}
            propKey="subtitle"
            setProp={setProp}
            enabled={enabled}
            selected={selected}
            as="span"
            multiline
            placeholder="Subtitle description..."
          />
        </p>

        <div className="flex flex-wrap gap-4 items-center">
          <button
            type="button"
            className="px-6 py-3.5 rounded-xl font-bold text-sm shadow-lg hover:opacity-95 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
            style={{
              backgroundColor: primaryColor,
              color: "#ffffff",
            }}
          >
            <InlineEditable
              value={primaryButtonText}
              propKey="primaryButtonText"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Primary Button"
            />
            <MdArrowForward size={18} />
          </button>

          {(secondaryButtonText || enabled) && (
            <button
              type="button"
              className="px-6 py-3.5 rounded-xl font-bold text-sm backdrop-blur-md transition-all hover:bg-white/20"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                color: secondaryColor,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              <InlineEditable
                value={secondaryButtonText}
                propKey="secondaryButtonText"
                setProp={setProp}
                enabled={enabled}
                selected={selected}
                as="span"
                placeholder="Secondary Button"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

HeroBanner.craft = {
  displayName: "Hero Banner Block",
  props: {
    eyebrow: "✨ NEW ARRIVALS 2026",
    title: "Discover Handcrafted Luxury & Modern Living",
    subtitle: "Explore our premium curated collection designed with utmost quality, style, and care for you.",
    primaryButtonText: "Explore Collection",
    secondaryButtonText: "View Special Offers",
    primaryColor: "#4f46e5",
    secondaryColor: "#ffffff",
    textColor: "#ffffff",
    bgGradient: "from-slate-900 via-indigo-950 to-slate-900",
    bgImage: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&auto=format&fit=crop&q=80",
    overlayOpacity: 65,
    minHeight: "480px",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 6. PRODUCT SHOWCASE BLOCK ─────────────────────────────────────────
export const ProductShowcase = ({
  sectionTitle = "Featured Products",
  sectionSubtitle = "Handpicked trending items loved by our customers",
  columns = 4,
  itemCount = 8,
  primaryColor = "#4f46e5",
  cardBackground = "#ffffff",
  borderRadius = 16,
  showRating = true,
  showBadge = true,
  viewAllText = "View All",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const {
    products: storeProducts = [],
    onAddToCart,
    bussinessUuid,
    displayBusinessName,
    previewMode,
  } = useCraftStore();

  const [addedProductId, setAddedProductId] = useState(null);

  const sampleProducts = [
    {
      _id: "sample-1",
      productId: "sample-1",
      productName: "Organic Artisan Green Tea",
      category: "Beverages",
      salePrice: 499,
      mrp: 699,
      rating: 4.9,
      image:
        "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=500&auto=format&fit=crop&q=80",
      tag: "Best Seller",
    },
    {
      _id: "sample-2",
      productId: "sample-2",
      productName: "Handcrafted Ceramic Mug",
      category: "Home & Living",
      salePrice: 349,
      mrp: 450,
      rating: 4.8,
      image:
        "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=500&auto=format&fit=crop&q=80",
      tag: "Popular",
    },
    {
      _id: "sample-3",
      productId: "sample-3",
      productName: "Botanical Pure Essential Oil",
      category: "Wellness",
      salePrice: 799,
      mrp: 999,
      rating: 5.0,
      image:
        "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=500&auto=format&fit=crop&q=80",
      tag: "New",
    },
    {
      _id: "sample-4",
      productId: "sample-4",
      productName: "Eco-friendly Cotton Tote Bag",
      category: "Fashion Accessories",
      salePrice: 299,
      mrp: 399,
      rating: 4.7,
      image:
        "https://images.unsplash.com/photo-1597484661643-2f5fef640dd1?w=500&auto=format&fit=crop&q=80",
      tag: "20% OFF",
    },
  ];

  const hasRealProducts = Array.isArray(storeProducts) && storeProducts.length > 0;
  const rawList = hasRealProducts ? storeProducts : sampleProducts;
  const displayProducts = rawList.slice(0, itemCount || columns * 2 || 8);

  const getProductImage = (prod) => {
    const raw =
      prod.itemPicture ||
      prod.productImage ||
      prod.thumbnail ||
      prod.image ||
      (Array.isArray(prod.images) && prod.images[0]);
    if (!raw) {
      return "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=500&auto=format&fit=crop&q=80";
    }
    if (typeof raw === "string" && (raw.startsWith("http://") || raw.startsWith("https://"))) {
      return raw;
    }
    return `${AWS_URL}${raw}`;
  };

  const getFormattedPrice = (priceVal) => {
    const num = parseFloat(priceVal);
    if (isNaN(num) || num <= 0) return "₹0";
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const getDiscountBadge = (prod) => {
    if (prod.tag) return prod.tag;
    const sale = parseFloat(prod.salePrice || prod.price || 0);
    const orig = parseFloat(prod.mrp || prod.originalPrice || 0);
    if (orig > sale && sale > 0) {
      const pct = Math.round(((orig - sale) / orig) * 100);
      if (pct > 0) return `${pct}% OFF`;
    }
    if (prod.isBestSeller) return "Best Seller";
    if (prod.isNew) return "New";
    return null;
  };

  const handleProductClick = (prod) => {
    if (enabled || previewMode) return;
    const prodId = prod.productId || prod._id || prod.id;
    if (prodId && bussinessUuid && displayBusinessName) {
      window.location.href = `/${displayBusinessName}/store/${bussinessUuid}/product/${prodId}`;
    }
  };

  const handleAddToCartClick = (e, prod) => {
    e.stopPropagation();
    const pId = prod.productId || prod._id || prod.id;
    setAddedProductId(pId);
    setTimeout(() => setAddedProductId(null), 1200);

    if (typeof onAddToCart === "function") {
      onAddToCart(prod);
    }
  };

  const gridColsClass =
    columns === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : columns === 3
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full py-8 my-4 transition-all ${
        selected
          ? "ring-2 ring-indigo-500 rounded-2xl"
          : hovered
          ? "ring-1 ring-indigo-300 rounded-2xl"
          : ""
      }`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight mb-1">
            <InlineEditable
              value={sectionTitle}
              propKey="sectionTitle"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Section Title"
            />
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <InlineEditable
              value={sectionSubtitle}
              propKey="sectionSubtitle"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Section Subtitle"
            />
          </p>
        </div>

        <div className="mt-3 sm:mt-0 flex items-center gap-3">
          {hasRealProducts && (
            <span className="text-xs text-gray-400 font-semibold bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full">
              {storeProducts.length} {storeProducts.length === 1 ? "Product" : "Products"}
            </span>
          )}
          <button
            type="button"
            className="text-sm font-bold hover:underline flex items-center gap-1 transition-opacity hover:opacity-80"
            style={{ color: primaryColor }}
          >
            <InlineEditable
              value={viewAllText}
              propKey="viewAllText"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="View All"
            />
            <MdArrowForward size={16} />
          </button>
        </div>
      </div>

      {/* Notice in builder mode if showing samples */}
      {enabled && !hasRealProducts && (
        <div className="mb-4 px-4 py-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
          <span>✨ Previewing sample items. Once you add products to your store, they will automatically appear here.</span>
        </div>
      )}

      {/* Products Grid */}
      <div className={`grid ${gridColsClass} gap-6`}>
        {displayProducts.map((prod, idx) => {
          const prodId = prod.productId || prod._id || prod.id || idx;
          const prodName = prod.productName || prod.name || "Product";
          const prodCategory = prod.category || prod.categoryName || "Collection";
          const prodPrice = prod.salePrice || prod.price || 0;
          const prodMrp = prod.mrp || prod.originalPrice || 0;
          const prodRating = prod.rating || 4.8;
          const badgeText = getDiscountBadge(prod);
          const isAdded = addedProductId === prodId;

          return (
            <div
              key={prodId}
              onClick={() => handleProductClick(prod)}
              className={`group border border-gray-200/80 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col ${
                !enabled ? "cursor-pointer" : ""
              }`}
              style={{
                backgroundColor: cardBackground,
                borderRadius: `${borderRadius}px`,
              }}
            >
              {/* Product Image */}
              <div className="relative h-48 sm:h-56 overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img
                  src={getProductImage(prod)}
                  alt={prodName}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 select-none"
                  draggable={false}
                  onError={(e) => {
                    e.target.src =
                      "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=500&auto=format&fit=crop&q=80";
                  }}
                />
                {showBadge && badgeText && (
                  <span
                    className="absolute top-3 left-3 px-2.5 py-1 text-[11px] font-bold rounded-full text-white shadow-md"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {badgeText}
                  </span>
                )}
              </div>

              {/* Product Details */}
              <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                <div>
                  <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">
                    {prodCategory}
                  </span>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white line-clamp-2 leading-snug group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {prodName}
                  </h3>
                  {showRating && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="flex text-amber-400">
                        <MdStar size={15} />
                      </div>
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                        {prodRating}
                      </span>
                    </div>
                  )}
                </div>

                {/* Price & Action */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-base sm:text-lg font-black text-gray-900 dark:text-white">
                      {getFormattedPrice(prodPrice)}
                    </span>
                    {parseFloat(prodMrp) > parseFloat(prodPrice) && (
                      <span className="text-xs text-gray-400 line-through">
                        {getFormattedPrice(prodMrp)}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleAddToCartClick(e, prod)}
                    className="p-2.5 rounded-xl text-white transition-all transform hover:scale-105 active:scale-95 shadow-md flex items-center justify-center"
                    style={{ backgroundColor: isAdded ? "#10b981" : primaryColor }}
                    title="Add to cart"
                  >
                    {isAdded ? <MdCheck size={18} /> : <MdShoppingBag size={18} />}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ProductShowcase.craft = {
  displayName: "Product Showcase Block",
  props: {
    sectionTitle: "Featured Products",
    sectionSubtitle: "Handpicked trending items loved by our customers",
    columns: 4,
    itemCount: 8,
    primaryColor: "#4f46e5",
    cardBackground: "#ffffff",
    borderRadius: 16,
    showRating: true,
    showBadge: true,
    viewAllText: "View All",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};


// ─── 7. FEATURES LIST BLOCK ───────────────────────────────────────────
export const FeaturesList = ({
  feature1Title = "Fast & Free Shipping",
  feature1Desc = "Free delivery on orders over ₹499",
  feature2Title = "Secure Payments",
  feature2Desc = "100% encrypted & trusted checkout",
  feature3Title = "24/7 Dedicated Support",
  feature3Desc = "Instant WhatsApp & Call assistance",
  feature4Title = "Easy 7-Day Returns",
  feature4Desc = "Hassle-free exchange policy",
  backgroundColor = "#f8fafc",
  iconColor = "#4f46e5",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const items = [
    { icon: MdLocalShipping, titleKey: "feature1Title", descKey: "feature1Desc", title: feature1Title, desc: feature1Desc },
    { icon: MdSecurity, titleKey: "feature2Title", descKey: "feature2Desc", title: feature2Title, desc: feature2Desc },
    { icon: MdHeadsetMic, titleKey: "feature3Title", descKey: "feature3Desc", title: feature3Title, desc: feature3Desc },
    { icon: MdShoppingBag, titleKey: "feature4Title", descKey: "feature4Desc", title: feature4Title, desc: feature4Desc },
  ];

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full p-6 sm:p-8 my-4 rounded-2xl border border-gray-100 shadow-sm transition-all ${
        selected ? "ring-2 ring-indigo-500" : hovered ? "ring-1 ring-indigo-300" : ""
      }`}
      style={{ backgroundColor }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={idx} className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                style={{
                  backgroundColor: `${iconColor}15`,
                  color: iconColor,
                }}
              >
                <Icon size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-gray-900 text-sm mb-0.5">
                  <InlineEditable
                    value={item.title}
                    propKey={item.titleKey}
                    setProp={setProp}
                    enabled={enabled}
                    selected={selected}
                    as="span"
                    placeholder="Feature Title"
                  />
                </h4>
                <p className="text-xs text-gray-500">
                  <InlineEditable
                    value={item.desc}
                    propKey={item.descKey}
                    setProp={setProp}
                    enabled={enabled}
                    selected={selected}
                    as="span"
                    placeholder="Feature Description"
                  />
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

FeaturesList.craft = {
  displayName: "Feature Highlights Block",
  props: {
    feature1Title: "Fast & Free Shipping",
    feature1Desc: "Free delivery on orders over ₹499",
    feature2Title: "Secure Payments",
    feature2Desc: "100% encrypted & trusted checkout",
    feature3Title: "24/7 Dedicated Support",
    feature3Desc: "Instant WhatsApp & Call assistance",
    feature4Title: "Easy 7-Day Returns",
    feature4Desc: "Hassle-free exchange policy",
    backgroundColor: "#f8fafc",
    iconColor: "#4f46e5",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 8. PROMO BANNER BLOCK ────────────────────────────────────────────
export const PromoBanner = ({
  badge = "LIMITED TIME OFFER",
  title = "Get 25% Off Your First Order!",
  subtitle = "Use code WELCOME25 at checkout. Valid on all premium collections.",
  couponCode = "WELCOME25",
  buttonText = "Claim Discount",
  backgroundColor = "#4f46e5",
  textColor = "#ffffff",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full my-4 rounded-2xl p-6 sm:p-10 shadow-lg text-center transition-all ${
        selected ? "ring-2 ring-indigo-500" : hovered ? "ring-1 ring-indigo-300" : ""
      }`}
      style={{
        backgroundColor,
        color: textColor,
      }}
    >
      {(badge || enabled) && (
        <div className="inline-block mb-3">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-black tracking-wider bg-white/20 backdrop-blur-md">
            <InlineEditable
              value={badge}
              propKey="badge"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Badge Text"
            />
          </span>
        </div>
      )}

      <h2 className="text-2xl sm:text-4xl font-black mb-2 tracking-tight">
        <InlineEditable
          value={title}
          propKey="title"
          setProp={setProp}
          enabled={enabled}
          selected={selected}
          as="span"
          multiline
          placeholder="Promo Title"
        />
      </h2>

      <p className="text-sm sm:text-base opacity-90 max-w-xl mx-auto mb-6">
        <InlineEditable
          value={subtitle}
          propKey="subtitle"
          setProp={setProp}
          enabled={enabled}
          selected={selected}
          as="span"
          multiline
          placeholder="Promo Subtitle"
        />
      </p>

      <div className="flex flex-wrap items-center justify-center gap-3">
        {(couponCode || enabled) && (
          <div className="px-4 py-2.5 rounded-xl border-2 border-dashed border-white/60 font-mono font-bold text-sm bg-white/10 tracking-wider">
            <InlineEditable
              value={couponCode}
              propKey="couponCode"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="COUPON20"
            />
          </div>
        )}
        <button
          type="button"
          className="px-6 py-2.5 rounded-xl font-bold text-sm bg-white text-gray-900 hover:bg-gray-100 shadow-md transition-all"
        >
          <InlineEditable
            value={buttonText}
            propKey="buttonText"
            setProp={setProp}
            enabled={enabled}
            selected={selected}
            as="span"
            placeholder="Button Label"
          />
        </button>
      </div>
    </div>
  );
};

PromoBanner.craft = {
  displayName: "Promo / Announcement Banner",
  props: {
    badge: "LIMITED TIME OFFER",
    title: "Get 25% Off Your First Order!",
    subtitle: "Use code WELCOME25 at checkout. Valid on all premium collections.",
    couponCode: "WELCOME25",
    buttonText: "Claim Discount",
    backgroundColor: "#4f46e5",
    textColor: "#ffffff",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 9. TESTIMONIALS BLOCK ────────────────────────────────────────────
export const TestimonialsSection = ({
  heading = "What Our Customers Say",
  subheading = "Real reviews from verified shoppers across the country",
  primaryColor = "#4f46e5",
  backgroundColor = "#ffffff",
  t1Name = "Priya Sharma",
  t1Role = "Verified Buyer",
  t1Comment = "Absolutely top-notch quality! Fast delivery and the product looks even better in real life than in photos.",
  t2Name = "Rahul Verma",
  t2Role = "Regular Customer",
  t2Comment = "Amazing customer support and super smooth checkout experience. Highly recommend this store to everyone!",
  t3Name = "Ananya Patel",
  t3Role = "Verified Buyer",
  t3Comment = "The attention to detail and packaging was incredible. Will definitely be placing more orders soon.",
  t1Avatar = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&auto=format&fit=crop&q=80",
  t2Avatar = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80",
  t3Avatar = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const testimonials = [
    {
      key: "t1",
      name: t1Name,
      role: t1Role,
      comment: t1Comment,
      rating: 5,
      avatar: t1Avatar,
    },
    {
      key: "t2",
      name: t2Name,
      role: t2Role,
      comment: t2Comment,
      rating: 5,
      avatar: t2Avatar,
    },
    {
      key: "t3",
      name: t3Name,
      role: t3Role,
      comment: t3Comment,
      rating: 5,
      avatar: t3Avatar,
    },
  ];

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full py-10 my-4 transition-all ${
        selected ? "ring-2 ring-indigo-500 rounded-2xl" : hovered ? "ring-1 ring-indigo-300 rounded-2xl" : ""
      }`}
      style={{ backgroundColor }}
    >
      <div className="text-center max-w-2xl mx-auto mb-10">
        <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mb-2">
          <InlineEditable
            value={heading}
            propKey="heading"
            setProp={setProp}
            enabled={enabled}
            selected={selected}
            as="span"
            placeholder="Section Heading"
          />
        </h2>
        <p className="text-sm text-gray-500">
          <InlineEditable
            value={subheading}
            propKey="subheading"
            setProp={setProp}
            enabled={enabled}
            selected={selected}
            as="span"
            placeholder="Section Subheading"
          />
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((item, idx) => (
          <div
            key={idx}
            className="p-6 rounded-2xl border border-gray-100 bg-gray-50/50 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex text-amber-400 mb-4">
                {[...Array(item.rating)].map((_, i) => (
                  <MdStar key={i} size={18} />
                ))}
              </div>
              <p className="text-sm text-gray-600 italic leading-relaxed mb-6">
                <InlineEditable value={item.comment} propKey={`${item.key}Comment`} setProp={setProp} enabled={enabled} selected={selected} as="span" multiline placeholder="Customer review" />
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <img
                  src={resolveImageUrl(item.avatar)}
                  alt={item.name}
                  className="w-10 h-10 rounded-full object-cover shadow-sm border border-white pointer-events-none select-none"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />
                <ImageChangeButton
                  enabled={enabled}
                  label=""
                  className="-bottom-1 -right-1 !p-1 !rounded-full"
                  onUploaded={(url) => setProp((p) => { p[`${item.key}Avatar`] = url; })}
                />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">
                  <InlineEditable value={item.name} propKey={`${item.key}Name`} setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Customer Name" />
                </h4>
                <span className="text-[11px] text-gray-400 font-medium">
                  <InlineEditable value={item.role} propKey={`${item.key}Role`} setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Customer Role" />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

TestimonialsSection.craft = {
  displayName: "Testimonials Block",
  props: {
    heading: "What Our Customers Say",
    subheading: "Real reviews from verified shoppers across the country",
    primaryColor: "#4f46e5",
    backgroundColor: "#ffffff",
    t1Name: "Priya Sharma",
    t1Role: "Verified Buyer",
    t1Comment: "Absolutely top-notch quality! Fast delivery and the product looks even better in real life than in photos.",
    t2Name: "Rahul Verma",
    t2Role: "Regular Customer",
    t2Comment: "Amazing customer support and super smooth checkout experience. Highly recommend this store to everyone!",
    t3Name: "Ananya Patel",
    t3Role: "Verified Buyer",
    t3Comment: "The attention to detail and packaging was incredible. Will definitely be placing more orders soon.",
    t1Avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&auto=format&fit=crop&q=80",
    t2Avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80",
    t3Avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 10. CONTACT & STORE INFO BLOCK ───────────────────────────────────
export const ContactSection = ({
  title = "Get In Touch With Us",
  subtitle = "Have questions or need assistance? We are here to help you anytime.",
  phone = "+91 98765 43210",
  email = "support@yourstore.com",
  address = "123 Commerce Avenue, Tech Hub, City, India",
  primaryColor = "#4f46e5",
  whatsappButtonText = "Chat on WhatsApp",
  callLabel = "Call Us",
  emailLabel = "Email Us",
  addressLabel = "Store Address",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full p-6 sm:p-10 my-4 rounded-2xl bg-gradient-to-br from-indigo-50/70 via-white to-purple-50/70 border border-indigo-100 shadow-sm transition-all ${
        selected ? "ring-2 ring-indigo-500" : hovered ? "ring-1 ring-indigo-300" : ""
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div>
          <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
            <InlineEditable
              value={title}
              propKey="title"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Contact Title"
            />
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            <InlineEditable
              value={subtitle}
              propKey="subtitle"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              multiline
              placeholder="Contact Subtitle"
            />
          </p>

          <button
            type="button"
            className="px-6 py-3 rounded-xl font-bold text-sm text-white shadow-md flex items-center gap-2 hover:opacity-95 transition-all"
            style={{ backgroundColor: "#25D366" }}
          >
            <FaWhatsapp size={20} />
            <InlineEditable
              value={whatsappButtonText}
              propKey="whatsappButtonText"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="WhatsApp Button Text"
            />
          </button>
        </div>

        <div className="space-y-4 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 mt-0.5">
              <MdPhone size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-gray-400 font-semibold uppercase block"><InlineEditable value={callLabel} propKey="callLabel" setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Call Us" /></span>
              <p className="text-sm font-bold text-gray-900">
                <InlineEditable
                  value={phone}
                  propKey="phone"
                  setProp={setProp}
                  enabled={enabled}
                  selected={selected}
                  as="span"
                  placeholder="+91 98765 43210"
                />
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 mt-0.5">
              <MdEmail size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-gray-400 font-semibold uppercase block"><InlineEditable value={emailLabel} propKey="emailLabel" setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Email Us" /></span>
              <p className="text-sm font-bold text-gray-900">
                <InlineEditable
                  value={email}
                  propKey="email"
                  setProp={setProp}
                  enabled={enabled}
                  selected={selected}
                  as="span"
                  placeholder="support@yourstore.com"
                />
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 mt-0.5">
              <MdLocationOn size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-gray-400 font-semibold uppercase block"><InlineEditable value={addressLabel} propKey="addressLabel" setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Store Address" /></span>
              <p className="text-sm font-bold text-gray-900">
                <InlineEditable
                  value={address}
                  propKey="address"
                  setProp={setProp}
                  enabled={enabled}
                  selected={selected}
                  as="span"
                  multiline
                  placeholder="Store Address Location"
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ContactSection.craft = {
  displayName: "Contact & Store Info Block",
  props: {
    title: "Get In Touch With Us",
    subtitle: "Have questions or need assistance? We are here to help you anytime.",
    phone: "+91 98765 43210",
    email: "support@yourstore.com",
    address: "123 Commerce Avenue, Tech Hub, City, India",
    primaryColor: "#4f46e5",
    whatsappButtonText: "Chat on WhatsApp",
    callLabel: "Call Us",
    emailLabel: "Email Us",
    addressLabel: "Store Address",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 11. FOOTER BLOCK ─────────────────────────────────────────────────
export const FooterSection = ({
  storeName = "Your Store Name",
  tagline = "Your favorite destination for quality products and seamless shopping.",
  copyright = "© 2026 All Rights Reserved. Powered by DukanBuddy.",
  backgroundColor = "#111827",
  textColor = "#9ca3af",
  headingColor = "#ffffff",
  quickLinksTitle = "Quick Links",
  customerCareTitle = "Customer Care",
  link1 = "Home",
  link2 = "All Products",
  link3 = "Special Offers",
  link4 = "Contact Us",
  link5 = "Order Tracking",
  link6 = "Shipping Policy",
  link7 = "Returns & Refunds",
  link8 = "Privacy Policy",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <footer
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full pt-12 pb-8 px-6 sm:px-12 my-4 rounded-2xl transition-all ${
        selected ? "ring-2 ring-indigo-500" : hovered ? "ring-1 ring-indigo-300" : ""
      }`}
      style={{ backgroundColor }}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 pb-8 border-b border-gray-800">
        <div className="md:col-span-2">
          <h3 className="text-xl font-black mb-2" style={{ color: headingColor }}>
            <InlineEditable
              value={storeName}
              propKey="storeName"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              placeholder="Store Name"
            />
          </h3>
          <p className="text-sm leading-relaxed max-w-sm" style={{ color: textColor }}>
            <InlineEditable
              value={tagline}
              propKey="tagline"
              setProp={setProp}
              enabled={enabled}
              selected={selected}
              as="span"
              multiline
              placeholder="Store tagline..."
            />
          </p>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: headingColor }}>
            <InlineEditable value={quickLinksTitle} propKey="quickLinksTitle" setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Quick Links" />
          </h4>
          <ul className="space-y-2 text-sm" style={{ color: textColor }}>
            {[[link1, "link1"], [link2, "link2"], [link3, "link3"], [link4, "link4"]].map(([val, key]) => (
              <li key={key}>
                <InlineEditable value={val} propKey={key} setProp={setProp} enabled={enabled} selected={selected} as="span" className="hover:text-white transition-colors" placeholder="Link" />
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: headingColor }}>
            <InlineEditable value={customerCareTitle} propKey="customerCareTitle" setProp={setProp} enabled={enabled} selected={selected} as="span" placeholder="Customer Care" />
          </h4>
          <ul className="space-y-2 text-sm" style={{ color: textColor }}>
            {[[link5, "link5"], [link6, "link6"], [link7, "link7"], [link8, "link8"]].map(([val, key]) => (
              <li key={key}>
                <InlineEditable value={val} propKey={key} setProp={setProp} enabled={enabled} selected={selected} as="span" className="hover:text-white transition-colors" placeholder="Link" />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="pt-6 text-center text-xs" style={{ color: textColor }}>
        <InlineEditable
          value={copyright}
          propKey="copyright"
          setProp={setProp}
          enabled={enabled}
          selected={selected}
          as="span"
          placeholder="© Copyright notice"
        />
      </div>
    </footer>
  );
};

FooterSection.craft = {
  displayName: "Footer Section Block",
  props: {
    storeName: "Your Store Name",
    tagline: "Your favorite destination for quality products and seamless shopping.",
    copyright: "© 2026 All Rights Reserved. Powered by DukanBuddy.",
    backgroundColor: "#111827",
    textColor: "#9ca3af",
    headingColor: "#ffffff",
    quickLinksTitle: "Quick Links",
    customerCareTitle: "Customer Care",
    link1: "Home",
    link2: "All Products",
    link3: "Special Offers",
    link4: "Contact Us",
    link5: "Order Tracking",
    link6: "Shipping Policy",
    link7: "Returns & Refunds",
    link8: "Privacy Policy",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};

// ─── 12. DIVIDER / SPACER COMPONENT ──────────────────────────────────
export const Divider = ({
  height = 32,
  showLine = true,
  lineColor = "#e5e7eb",
  lineWidth = 1,
  lineStyle = "solid",
}) => {
  const {
    connectors: { connect, drag },
    selected,
    hovered,
  } = useNode((node) => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={`w-full flex items-center justify-center transition-all ${
        selected ? "ring-2 ring-indigo-500 rounded p-1" : hovered ? "ring-1 ring-indigo-300 rounded p-1" : ""
      }`}
      style={{
        height: `${height}px`,
        margin: "8px 0",
      }}
    >
      {showLine && (
        <div
          className="w-full"
          style={{
            borderTop: `${lineWidth}px ${lineStyle} ${lineColor}`,
          }}
        />
      )}
    </div>
  );
};

Divider.craft = {
  displayName: "Divider / Spacer",
  props: {
    height: 32,
    showLine: true,
    lineColor: "#e5e7eb",
    lineWidth: 1,
    lineStyle: "solid",
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
    canMoveOut: () => true,
  },
};
